import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { darkTheme, lightTheme, ThemeMode } from '../../config/theme';

const THEME_STORAGE_KEY = 'hs-theme-mode';

// Union type for both themes
type AppTheme = typeof lightTheme | typeof darkTheme;

interface ThemeContextValue {
  theme: AppTheme;
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeModeProvider: FC<ThemeProviderProps> = ({ children }) => {
  // Always use light mode
  const themeMode: ThemeMode = 'light';

  const theme = useMemo(
    () => lightTheme,
    [],
  );

  const isDarkMode = false;

  const setThemeMode = useCallback((mode: ThemeMode) => {
    // Do nothing - theme is locked to light mode
  }, []);

  const toggleTheme = useCallback(() => {
    // Do nothing - theme is locked to light mode
  }, []);

  // Apply light theme class to document for Tailwind and clear old theme preference
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
    // Clear any old theme preference from localStorage
    localStorage.removeItem(THEME_STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      themeMode,
      toggleTheme,
      setThemeMode,
      isDarkMode,
    }),
    [theme, themeMode, toggleTheme, setThemeMode, isDarkMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};
